// skills/mining/mining.js
// Mining skill logic

import { ROCKS, PICKAXES } from './miningData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { awardDrops, selectWeightedDrop, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import {
    getTalentLootBonus,
    getTalentFlatBonus,
    rollTalentProc,
    rollGlobalRareLoot,
    incrementStackCount,
    resetStackCount
} from '../../systems/talentTree/talentTree.js';
import { BARS } from '../smithing/smithingData.js';
import { getItemName } from '../../data/items/index.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { bountySkill } from '../bounty/bounty.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class MiningSkill extends BaseSkill {
    constructor() {
        super(SKILLS.MINING, PICKAXES, 'PYRESTONE');
    }

    /**
     * Get the currently equipped pickaxe object (alias for BaseSkill.getCurrentTool)
     */
    get currentPickaxe() {
        return this.getCurrentTool();
    }

    /**
     * Check if player can mine a rock
     */
    canMine(rockId) {
        // Find rock by id property, not by object key
        const rock = Object.values(ROCKS).find(r => r.id === rockId);
        if (!rock) return { success: false, reason: 'Rock not found' };

        const skill = gameState.getSkill(SKILLS.MINING);
        if (skill.level < rock.level) {
            return { success: false, reason: `Requires level ${rock.level} Mining` };
        }

        // Check if primary drop can fit in inventory
        const primaryDrop = rock.drops[0];
        if (primaryDrop && !canFitItem(primaryDrop.itemId)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start mining a rock
     */
    startMining(rockId) {
        // Find rock by id property, not by object key
        const rock = Object.values(ROCKS).find(r => r.id === rockId);
        const canMine = this.canMine(rockId);

        if (!canMine.success) {
            logger.debug('Cannot mine:', canMine.reason);
            return false;
        }

        // Calculate mining time based on rock and effective speed multiplier
        const baseTime = rock.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const miningTime = baseTime / speedMultiplier;

        // Create mining action
        const action = {
            type: 'mining',
            name: `Mining ${rock.name}`,
            rock: rock,
            rockId: rockId.toUpperCase(),
            progress: 0,
            duration: miningTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            // speedMultiplier param includes admin speed + corruption (applied in tick.js)
            tick: (speedMultiplier = 1) => {
                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    const result = this.completeMining(rock);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - mining stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canMine(rockId);
                    if (canContinue.success) {
                        this.startMining(rockId);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                // Complete when progress reaches 1
                if (action.progress >= 1) {
                    const result = this.completeMining(rock);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - mining stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canMine(rockId);
                    if (canContinue.success) {
                        this.startMining(rockId);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete a mining action
     * @returns {{ inventoryFull: boolean }} Whether inventory became full
     */
    completeMining(rock) {
        // Award XP (pass rock.id for admin node XP override)
        const xpResult = addXP(SKILLS.MINING, rock.xp, rock.id);

        // Prepare drops with talent bonuses
        let drops;
        const hasWeights = rock.drops.some(d => d.weight && d.weight !== 1);

        if (hasWeights) {
            // Weighted drop (gem rocks) - select one
            const selectedDrop = selectWeightedDrop(rock.drops);
            drops = [{ ...selectedDrop }];
        } else {
            // Regular drops - copy for modification
            drops = rock.drops.map(d => ({ ...d }));
        }

        // === TALENT PROCS ===

        // Get Grand Prospector bonus (boosts all drop proc chances by 25%)
        const dropChanceBonus = getTalentFlatBonus(SKILLS.MINING, 'dropChanceBonus');

        // 1. Apply loot bonus (Rich Veins) - increases base quantity
        const lootBonus = getTalentLootBonus(SKILLS.MINING);
        if (lootBonus > 0) {
            drops.forEach(drop => {
                drop.quantity = Math.floor(drop.quantity * (1 + lootBonus));
                if (drop.quantity < 1) drop.quantity = 1;
            });
        }

        // Note: Contract Momentum bonus is applied centrally in awardDrops()

        // 2. Roll for quantity multipliers (check highest first, they don't stack)
        let quantityMultiplier = 1;
        let procMessage = null;
        const primaryOreId = drops[0]?.itemId;

        // Jackpot (25x) - from Motherlode capstone
        if (rollTalentProc(SKILLS.MINING, 'jackpot', dropChanceBonus)) {
            quantityMultiplier = 25;
            procMessage = 'JACKPOT! 25x ore!';
            recordTalentProc(SKILLS.MINING, 'Jackpot', null, null, { itemId: primaryOreId, quantity: 25 });
        } else if (rollTalentProc(SKILLS.MINING, 'motherlode', dropChanceBonus)) {
            quantityMultiplier = 10;
            procMessage = 'Motherlode! 10x ore!';
            recordTalentProc(SKILLS.MINING, 'Motherlode', null, null, { itemId: primaryOreId, quantity: 10 });
        } else if (rollTalentProc(SKILLS.MINING, 'pureVein', dropChanceBonus)) {
            quantityMultiplier = 5;
            procMessage = 'Pure Vein! 5x ore!';
            recordTalentProc(SKILLS.MINING, 'Pure Vein', null, null, { itemId: primaryOreId, quantity: 5 });
        } else if (rollTalentProc(SKILLS.MINING, 'doubleOre', dropChanceBonus)) {
            quantityMultiplier = 2;
            procMessage = 'Keen Eye! Double ore!';
            recordTalentProc(SKILLS.MINING, 'Keen Eye', null, null, { itemId: primaryOreId, quantity: 2 });
        }

        if (quantityMultiplier > 1) {
            drops.forEach(drop => {
                drop.quantity *= quantityMultiplier;
            });
        }

        // 3. Award the main drops
        const dropResult = awardDrops(SKILLS.MINING, drops);

        // 4. Roll for bonus item procs
        // Get gem-related bonuses
        const gemDropFlat = getTalentFlatBonus(SKILLS.MINING, 'gemDropFlat');
        const gemQuantityBonus = getTalentFlatBonus(SKILLS.MINING, 'gemQuantity');
        const gemCount = gemQuantityBonus > 0 ? 2 : 1; // Gem Bonanza gives 2 gems

        // Gem Finder - chance to find random gems (base proc + flat bonus)
        const gemProcced = rollTalentProc(SKILLS.MINING, 'gemDrop', dropChanceBonus);
        const flatGemProcced = gemDropFlat > 0 && Math.random() < gemDropFlat;

        if (gemProcced || flatGemProcced) {
            // Scale available gems by mining level with tier-based weighting
            const randomGem = this.getWeightedGem();
            gameState.addItem(randomGem, gemCount);
            bountySkill.trackItemProgress(randomGem, gemCount);
            const talentName = gemCount > 1 ? 'Gem Bonanza' : 'Gem Finder';
            recordTalentProc(SKILLS.MINING, talentName, null, null, { itemId: randomGem, quantity: gemCount });
        }

        // Rare Earth - chance for bonus random ore type
        if (rollTalentProc(SKILLS.MINING, 'bonusOre', dropChanceBonus)) {
            const ores = ['pyrestone_ore', 'pale_shard_ore', 'blackiron_ore', 'deepcoal', 'gold_ore', 'skyiron_ore', 'verdant_core_ore', 'voidsteel_ore'];
            const randomOre = ores[Math.floor(Math.random() * ores.length)];
            gameState.addItem(randomOre, 1);
            bountySkill.trackItemProgress(randomOre, 1);
            recordTalentProc(SKILLS.MINING, 'Rare Earth', null, null, { itemId: randomOre, quantity: 1 });
        }

        // Heart of the Mountain - chance to auto-smelt ore into bar
        if (rollTalentProc(SKILLS.MINING, 'autoSmelt', dropChanceBonus)) {
            this.tryAutoSmelt(rock);
        }

        // Treasure Nose (Husbandry) - global rare loot bonus
        if (rollGlobalRareLoot()) {
            const randomGem = this.getWeightedGem();
            gameState.addItem(randomGem, gemCount);
            bountySkill.trackItemProgress(randomGem, gemCount);
            recordTalentProc(SKILLS.MINING, 'Treasure Nose', null, null, { itemId: randomGem, quantity: gemCount });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.MINING);

        // Check for pilferer contract assistance
        pilferingSkill.tryContractAssist('mining');

        // Increment stacking speed count (Steady Output talent)
        incrementStackCount(SKILLS.MINING, rock.id);

        // Notify UI of completion
        this.onMiningComplete(rock, xpResult);

        return { inventoryFull: dropResult.inventoryFull };
    }

    /**
     * Try to auto-smelt the mined ore into a bar (Heart of the Mountain talent)
     * Only works if player has sufficient Smithing level
     * @param {Object} rock - The rock being mined
     */
    tryAutoSmelt(rock) {
        // Map ore to bar
        const oreToBar = {
            'pyrestone_ore': 'PYRESTONE',
            'pale_shard_ore': 'PALE_SHARD',
            'blackiron_ore': 'BLACKIRON',
            'gold_ore': 'GOLD',
            'skyiron_ore': 'SKYIRON',
            'verdant_core_ore': 'VERDANT_CORE',
            'voidsteel_ore': 'VOIDSTEEL'
        };

        // Get the primary ore drop from this rock
        const primaryDrop = rock.drops[0];
        if (!primaryDrop) return;

        const barKey = oreToBar[primaryDrop.itemId];
        if (!barKey) return; // Not an ore rock (e.g., gem rock)

        const bar = BARS[barKey];
        if (!bar) return;

        // Check if player has sufficient Smithing level
        const smithingLevel = gameState.getSkill(SKILLS.SMITHING).level;
        if (smithingLevel < bar.level) return;

        // Award the bar and Smithing XP
        const barDrop = bar.drops[0];
        if (barDrop) {
            gameState.addItem(barDrop.itemId, 1);
            bountySkill.trackItemProgress(barDrop.itemId, 1);
            addXP(SKILLS.SMITHING, bar.xp, bar.id);
            recordTalentProc(SKILLS.MINING, 'Heart of the Mountain', null, null, { itemId: barDrop.itemId, quantity: 1 });
        }
    }

    /**
     * Get a weighted random gem based on mining level
     * Higher-tier gems are rarer, unlocked at higher levels
     */
    getWeightedGem() {
        const miningLevel = gameState.getSkill(SKILLS.MINING).level;
        const gemPool = [
            { id: 'sapphire', weight: 6 },
            { id: 'emerald', weight: 5 }
        ];
        if (miningLevel >= 20) gemPool.push({ id: 'ruby', weight: 4 });
        if (miningLevel >= 40) gemPool.push({ id: 'diamond', weight: 3 });
        if (miningLevel >= 60) gemPool.push({ id: 'astryx_stone', weight: 2 });
        if (miningLevel >= 80) gemPool.push({ id: 'onyx', weight: 1 });

        const totalWeight = gemPool.reduce((sum, g) => sum + g.weight, 0);
        let roll = Math.random() * totalWeight;
        for (const gem of gemPool) {
            roll -= gem.weight;
            if (roll <= 0) return gem.id;
        }
        return gemPool[0].id;
    }

    /**
     * Stop current mining action
     */
    stopMining() {
        resetStackCount(SKILLS.MINING);
        gameState.clearAction();
    }

    /**
     * Purchase or equip a pickaxe
     */
    upgradePickaxe(pickaxeId) {
        const pickaxe = Object.values(PICKAXES).find(p => p.id === pickaxeId);
        if (!pickaxe) return { success: false, reason: 'Invalid pickaxe' };

        const skill = gameState.getSkill(SKILLS.MINING);
        if (skill.level < pickaxe.level) {
            return { success: false, reason: `Requires level ${pickaxe.level} Mining` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.MINING, pickaxeId)) {
            // Just equip it
            gameState.equipTool(SKILLS.MINING, pickaxeId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.MINING, pickaxeId, pickaxe.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.MINING, pickaxeId);
        return { success: true, action: 'purchased' };
    }

    /**
     * Get available rocks for player's level
     */
    getAvailableRocks() {
        const skill = gameState.getSkill(SKILLS.MINING);
        return Object.values(ROCKS).filter(rock => rock.level <= skill.level);
    }

    /**
     * Callback for when mining completes (override in UI)
     */
    onMiningComplete(rock, xpResult) {
        logger.game(`Mined ${rock.name}! +${rock.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Mining level up! Now level ${xpResult.newLevel}`);
        }
    }
}

// Singleton instance
export const miningSkill = new MiningSkill();
export default miningSkill;